export const TICKET_TYPES = [
    'escalation',
    'project',
    'quote',
    'chat',
    'administrative',
];
export const TICKET_STATUSES = [
    'new',
    'backlog',
    'open',
    'in_progress',
    'scoping',
    'scoping_meeting_required',
    'quoting',
    'quote_sent',
    'accepted',
    'data_migration_in_progress',
    'scheduled_session',
    'client_responded',
    'waiting_on_client',
    'ping_kris',
    'on_hold',
    'waiting_on_third_party',
    'solved',
    'cancelled',
    'dead',
    'project_completed_balance_due',
];
export const STATUS_GROUPS = {
    Initial: ['new'],
    Backlog: ['backlog'],
    Open: [
        'open',
        'in_progress',
        'scoping',
        'scoping_meeting_required',
        'quoting',
        'quote_sent',
        'accepted',
        'data_migration_in_progress',
        'scheduled_session',
    ],
    'Pending client': ['client_responded', 'waiting_on_client', 'ping_kris'],
    'On hold': ['on_hold', 'waiting_on_third_party'],
    Closed: ['solved', 'cancelled', 'dead', 'project_completed_balance_due'],
};
// A ticket is "closed" for client-facing counting when it lands in the Closed
// group. Backlog is deliberately *not* closed — it is triaged work waiting its
// turn — but the portal surfaces it as its own bucket rather than mixing it
// into "Open", so a client can tell in-flight work from queued work.
export const CLOSED_TICKET_STATUSES = STATUS_GROUPS.Closed;
const CLOSED_TICKET_STATUS_SET = new Set(CLOSED_TICKET_STATUSES);
export function isClosedTicketStatus(status) {
    return CLOSED_TICKET_STATUS_SET.has(status);
}
export function isBacklogTicketStatus(status) {
    return status === 'backlog';
}
// Ticket types a CLIENT may park in the backlog, at submission or later.
// Quote requests are left out: a request IS "a quote ticket at `new` with
// no document" (services/quoteRequests.ts), so parking one would take it
// off the Quotes board. Chat is a system flow.
export const CLIENT_BACKLOG_TYPES = ['escalation', 'project', 'administrative'];
// The quote workflow statuses belong to the people quoting, not the client.
const CLIENT_BACKLOG_BLOCKED_STATUSES = new Set([
    'quoting',
    'quote_sent',
    'accepted',
]);
/** Whether a client may move this ticket INTO the backlog. One rule for
 *  the portal button and the server's gate. */
export function canClientMoveToBacklog(type, status) {
    return (CLIENT_BACKLOG_TYPES.includes(type) &&
        !isClosedTicketStatus(status) &&
        !isBacklogTicketStatus(status) &&
        !CLIENT_BACKLOG_BLOCKED_STATUSES.has(status));
}
/** Where a ticket goes when the client takes it OUT of the backlog: the
 *  triage queue if nobody has it yet, otherwise back to its technician. */
export function statusLeavingBacklog(assignedAgentId) {
    return assignedAgentId ? 'open' : 'new';
}
export const STATUS_TONES = {
    new: 'green',
    backlog: 'purple',
    open: 'green',
    in_progress: 'green',
    scoping: 'green',
    scoping_meeting_required: 'green',
    quoting: 'green',
    quote_sent: 'green',
    accepted: 'green',
    data_migration_in_progress: 'green',
    scheduled_session: 'purple',
    client_responded: 'red',
    waiting_on_client: 'purple',
    ping_kris: 'red',
    on_hold: 'purple',
    waiting_on_third_party: 'purple',
    solved: 'grey',
    cancelled: 'grey',
    dead: 'grey',
    project_completed_balance_due: 'grey',
};
// Statuses hidden from the agent dropdown by default. Admins can restore
// any of these from /admin/settings; a hidden status that is the *current*
// status of a ticket is still shown so agents can move off it. Existing
// tickets are not migrated — these are legacy values that remain valid in
// the DB.
export const DEFAULT_HIDDEN_TICKET_STATUSES = [
    'in_progress',
    'scoping_meeting_required',
    'quote_sent',
    'accepted',
    'data_migration_in_progress',
    'ping_kris',
    'dead',
];
// Time that does not bill the client must say why, in its notes - on a
// ticket or not. Every bucket outside block hours, project and quote counts,
// chat assistance included: it sits on the Non-billable side of the timer.
export const REASON_REQUIRED_BUCKETS = [
    'non_billable',
    'non_billable_pm',
    'non_billable_internal',
    'chat_assist',
    'comp_time_used',
];
/**
 * `bucket` is what the entry bills as. For a ticketed timer started without
 * one, pass the bucket the server will derive at stop.
 */
export function nonBillableReasonRequired(bucket) {
    return !!bucket && REASON_REQUIRED_BUCKETS.includes(bucket);
}
export const REASON_REQUIRED_MESSAGE = 'Non-billable time needs a reason. Say what the time was for.';
export const PROJECT_FINAL_PAYMENT_LABELS = {
    not_due: 'Not due yet',
    to_collect: 'Collect final payment',
    collected: 'Final payment collected',
};
export function projectFinalPayment(p) {
    if (p.billingMode !== 'flat_fee')
        return null;
    if (p.paymentCollected)
        return 'collected';
    return p.finalPaymentRequestedAt ? 'to_collect' : 'not_due';
}
/**
 * The subject as shown to users, prefixed with the client's own ticket
 * number when one was supplied: "TS10058 - Intune configuration"
 * (space-dash-space). Used for on-screen display and for outbound email
 * subject lines so the client sees their reference in replies. Falls back
 * to the plain subject when there is no client ticket number.
 */
export function ticketDisplaySubject(clientTicketNumber, subject) {
    const ref = clientTicketNumber?.trim();
    return ref ? `${ref} - ${subject}` : subject;
}
// A single time entry can never exceed 24h — `time_entries.duration_minutes`
// has carried that CHECK since migration 0005. The timer has to know the
// number too: committing a longer elapsed time would be rejected by the
// database and leave the timer running forever (see RUNAWAY_TIMER_MINUTES).
export const MAX_TIME_ENTRY_MINUTES = 24 * 60;
// Past this much elapsed time a timer is assumed forgotten rather than worked.
// Stopping it no longer logs the wall-clock elapsed time silently: the agent
// (or a time admin) is asked how long the work actually took, or discards it.
// Well beyond any real working session, and safely under the 24h hard cap.
export const RUNAWAY_TIMER_MINUTES = 12 * 60;
//# sourceMappingURL=ticket.js.map